import { ServiceNameType, type User } from '@consigli/types';

import {
  type AgentCategory,
  type AgentTag,
  type ServiceConfigEntry,
  serviceConfig,
} from './service-config';

/** A `serviceConfig` entry that is on product's directory list: `categories` and `tags` are set. */
export type CatalogueEntry = ServiceConfigEntry & {
  categories: readonly AgentCategory[];
  tags: readonly AgentTag[];
};

/** The three user fields the catalogue reads; a full `User` satisfies it. */
export type CatalogueViewer = Pick<
  User,
  'isSuperuser' | 'solutionViewerDatacenterAccess' | 'solutionViewerWaterTreatmentAccess'
>;

/** Categorised, but kept off the directory and the loading strip. */
const HIDDEN_AGENTS: ReadonlySet<ServiceNameType> = new Set([ServiceNameType.WASTEWATER_TREATMENT]);

/**
 * The agents the directory can show: every `serviceConfig` entry with
 * `categories`, less the hidden ones. Derived from the config, so a map keyed by
 * it fails type checking the moment an agent joins the directory without an
 * entry. Keep the `Exclude` in step with `HIDDEN_AGENTS`.
 */
export type DirectoryServiceName = Exclude<
  Extract<(typeof serviceConfig)[number], { categories: readonly AgentCategory[] }>['serviceName'],
  ServiceNameType.WASTEWATER_TREATMENT
>;

/**
 * The agents the directory shows this user, in `serviceConfig` order.
 *
 * Which agents that is, is product's list rather than a rule: the entries that
 * carry `categories` in `serviceConfig` — eighteen at the time of writing. One
 * rule survives, because it is a licence rather than a taxonomy: Data Center and
 * Wastewater treatment are listed but sold per user, so a user without the
 * matching flag does not see that card.
 *
 * Shared by `useAgentsDirectory` (the page) and `AgentLogoMarquee` (the loading
 * strip), so the strip cannot advertise something the page would not list.
 * Unsorted: the page sorts by translated title and the strip by English title,
 * for reasons each records.
 */
export const agentsCatalogue = (user: CatalogueViewer): CatalogueEntry[] =>
  // flatMap, not filter-then-map: the guard narrows `categories` and `tags`
  // away from undefined for the rest of the callback, which a filter cannot
  // do across the call boundary.
  serviceConfig.flatMap((service) => {
    const { categories, tags } = service;
    if (categories === undefined || tags === undefined) return [];
    if (HIDDEN_AGENTS.has(service.serviceName)) return [];

    if (
      service.serviceName === ServiceNameType.DATA_CENTER &&
      !user.isSuperuser &&
      !user.solutionViewerDatacenterAccess
    ) {
      return [];
    }

    if (
      service.serviceName === ServiceNameType.WASTEWATER_TREATMENT &&
      !user.isSuperuser &&
      !user.solutionViewerWaterTreatmentAccess
    ) {
      return [];
    }

    return [{ ...service, categories, tags }];
  });
